import React, { useEffect, useRef, useState } from 'react';
import FormTextField from './form-textfield';
import AsyncSelect from "react-select/async";
import GlSegmentSelect from './gl-segment-select';
import { inject } from "mobx-react";
import { cachedFetch } from '../utils/cached-api-wrapper';
import { buildOfficesSearchPath } from '../utils/offices-search';
import { ADERANT_LINE_REFERENCE_MAXLENGTH } from '../utils/aderant-field-limits';
import { isEditorAdditionalFieldAllowed } from '../utils/additional-fields-filter-helpers';
import { toPersistableOption } from '../utils/persistable-option';
import { getLineSplitField } from '../utils/line-split-fields';
import LineSplitInput from './line-split-input/line-split-input';
import Utils, {toNumber} from '../utils/utils';
import {
    buildRateLookupUrl,
    formatRateSourceHint,
    shouldLockToBillForAutoCalc,
} from '../utils/disbursement-auto-calc';
// Debounce window for the auto-calc fetch — short enough that the user
// sees the result almost immediately when they stop typing, long enough
// that we don't fire a request per keystroke as they type a quantity.
const AUTO_CALC_DEBOUNCE_MS = 300;

const loadOption = (fieldName, search_query = '', callback = () => {}) => {
    const val = (search_query || '').trim().replace(/\//g, '');
    const filters = val.length > 0 ? [{ id: 'Description', value: val }] : [];
    const searchData = `?field_name=${fieldName}&filters=${encodeURIComponent(JSON.stringify(filters))}`;
    cachedFetch(`/client/options/${searchData}`, 'GET', {}, (response) => {
        const rows = (response?.data ?? []).map((r) => ({ value: r.Id, label: r.Description }));
        callback(rows.slice(0, 10));
    });
};
// Employee rows carry `office` / `department` in addition to {value, label}
// so onChange can auto-populate matching line fields on selection.
const loadEmployees = (search_query = '', callback = () => {}) => {
    const val = (search_query || '').trim().replace(/\//g, '');
    const filters = val.length > 0 ? [{ id: 'Description', value: val }] : [];
    const searchData = `?field_name=Employee&filters=${encodeURIComponent(JSON.stringify(filters))}`;
    cachedFetch(`/client/options/${searchData}`, 'GET', {}, (response) => {
        const rows = (response?.data ?? []).map((r) => ({
            value: r.Id,
            label: r.Description,
            office: (r.office ?? '').toString().trim(),
            department: (r.department ?? '').toString().trim(),
        }));
        callback(rows.slice(0, 10));
    });
};
const loadProfitCenters = (q, cb) => loadOption('ProfitCenter', q, cb);
const loadJurisdictions = (q, cb) => loadOption('Jurisdiction', q, cb);
const loadLocations = (q, cb) => loadOption('Location', q, cb);
const loadProjects = (q, cb) => loadOption('Project', q, cb);
const loadDepartments = (q, cb) => loadOption('Department', q, cb);

// GL Section / Practice Group pickers (option-backed, code->name resolution) are
// shared with the invoice header via GlSegmentSelect (./gl-segment-select).

// Dropdowns whose upstream master can legitimately be empty per tenant
// (e.g. some firms have zero Locations rows). Probed on mount; fields whose
// result comes back empty are hidden so users don't see dead dropdowns. Three
// Employee flavors share the same probe (same upstream entity, cached at the
// network layer).
const PROBED_DROPDOWN_FIELDS = {
    Employee: loadEmployees,
    ProfitCenter: loadProfitCenters,
    Jurisdiction: loadJurisdictions,
    Location: loadLocations,
    Project: loadProjects,
    Department: loadDepartments,
};
const EMPLOYEE_FLAVORS = ['Employee', 'IncurringEmployee', 'AuthorizingEmployee'];
// XPEN-3025: dropdowns whose sole valid value should auto-fill the line and
// lock the field. When /client/options returns exactly one active row for one
// of these fields, the value is applied to every line item that has not yet
// been set and the input becomes read-only. Other fields keep the standard
// editable dropdown regardless of how many options come back.
const AUTO_LOCK_WHEN_SINGLE_OPTION = new Set(['ProfitCenter']);
const AUTO_LOCK_JSON_KEY_BY_FIELD = { ProfitCenter: 'ProfitCenterId' };
const ADDITIONAL_FIELD_BOLD_IDS = {
    Office: 'LineOffice',
    GLDepartement: 'LineGLDepartement',
    GLSection: 'LineGLSection',
    Jurisdiction: 'LineJurisdiction',
    HoldDate: 'LineHoldDate',
    Comments: 'LineComments',
};

// React elements use a `Symbol.for('react.element')` $$typeof tag that
// survives in memory but is dropped by JSON.stringify, so a re-loaded
// {value, label: <JSX>} option comes back as {value, label: { _owner,
// _store, key, props, ref, ... }}. Rendering that "label" trips React's
// invariant ("Objects are not valid as a React child"). Detect the
// post-JSON shape and substitute a plain-string label built from the
// surrounding data.
const REACT_ELEMENT_SYMBOL = Symbol.for('react.element');
const isRenderableLabel = (label) => {
    if (label == null) return true;
    const t = typeof label;
    if (t === 'string' || t === 'number' || t === 'boolean') return true;
    if (t === 'object' && label.$$typeof === REACT_ELEMENT_SYMBOL) return true;
    return false;
};
const toSelectOption = (stored) => {
    if (!stored || typeof stored !== 'object') return null;
    if (isRenderableLabel(stored.label)) return stored;
    const { value, code, name, Code, Description } = stored;
    const c = code ?? Code;
    const d = name ?? Description;
    const safeLabel = c && d ? `${c} - ${d}` : (c ?? d ?? (value != null ? String(value) : ''));
    return { ...stored, label: safeLabel };
};

const AdditionalFieldsModal = ({
    data,
    workItem,
    saving,
    locked,
    updateInvoice,
    fields = [],
    boldFieldIds = [],
    hiddenFieldIds = [],
    focusMode = false,
    isCommentsRequired = false,
    // XPEN-3151 line split: meant for the invoice screen only (wired in XPEN-3579);
    // the expense and card screens never pass them. Without onApplyLineSplit the
    // GL Timekeeper and Employee fields render exactly as before.
    //   onApplyLineSplit(workItem, rows) - rows are [{ option, percentage }];
    //   lineSplitDisabledReason - already translated text, or null;
    //   lineSplitNotice - { viewId, text }, shown only on the line with that _view_id.
    onApplyLineSplit,
    lineSplitDisabledReason = null,
    lineSplitNotice = null,
}) => {
    const isAderant = Utils.isExportFormat(data, 'aderant');
    const isElite = Utils.isExportFormat(data, 'elite');
    // Matches invoice.js isItemBillable — only true / 1 counts as billable.
    const isBillableLine = workItem?.IsBillable === true || workItem?.IsBillable === 1;
    const activityMap = (activity) => {
        if (typeof activity !== "object" || activity === null) {
            return null;
        }

        const activity_description = activity.description;
        const activity_code = activity.code;
        const activity_id = activity.activity_id;

        if ((activity_description || activity_code) && activity_id) {
            const option = { description: activity_description, code: activity_code, activity_id: activity_id, activity_list: activity.activity_list };
            return {
                value: option,
                label: (
                    <>
                        <strong>{activity_code}</strong> - {activity_description}
                    </>
                )
            };
        }
        return null;
    };

    const loadActivities = (search_query = "", callback = () => {
    }) => {
        let matter_id = workItem?.MatterId ?? "";
        const val = search_query.trim().replace(/\//g, '');
        const searchData = `${encodeURIComponent(val)}?matter_id=${matter_id}`;
        cachedFetch(`/activities/search/${searchData}`, "GET", {}, (response) => {
            const data = response.data;
            callback(data.map(activityMap));
        });
    };

    const loadOffices = (search_query = "", callback = () => {
    }) => {
        const searchData = buildOfficesSearchPath(search_query, data.id_client);
        cachedFetch(`/offices/${searchData}`, "GET", {}, (response) => {
            callback(response.data.offices.map(o => ({ label: o.name, value: o.name })));
        });
    };

    const shouldShowField = (fieldName) => {
        const fieldId = ADDITIONAL_FIELD_BOLD_IDS[fieldName] || fieldName;
        return !hiddenFieldIds.includes(fieldId) && (fields.length === 0 || fields.includes(fieldName));
    };
    const boldLabelClass = (fieldName) => (
        boldFieldIds.includes(ADDITIONAL_FIELD_BOLD_IDS[fieldName] || fieldName) ? 'font-weight-bold' : ''
    );
    const boldValueClass = (fieldName) => (
        boldFieldIds.includes(ADDITIONAL_FIELD_BOLD_IDS[fieldName] || fieldName) ? 'font-weight-bold font-weight-bolder' : ''
    );

    // Per-field option count for dropdowns. Undefined = still probing, 0 =
    // hide the field, > 0 = render. Three Employee flavors share one probe
    // (same upstream entity).
    const [optionCounts, setOptionCounts] = useState({});
    // XPEN-3025: sole row captured when a probed field returns exactly one
    // active option — feeds the auto-fill effect below and the read-only
    // gate in renderDropdownField. Populated only for fields in
    // AUTO_LOCK_WHEN_SINGLE_OPTION.
    const [singleOption, setSingleOption] = useState({});

    // Auto-calculation state for the To-Bill field (XPEN-2524).
    // `autoCalcInfo` holds the last `{rate_source, reason}` response.
    // `manualToBillBackup` snapshots the value the user had typed before
    // toggling on, so toggling off restores it (the ticket's "don't lose
    // the value the user previously typed" guarantee).
    const [autoCalcInfo, setAutoCalcInfo] = useState({ rate_source: null, reason: null });
    const [manualToBillBackup, setManualToBillBackup] = useState(undefined);
    const prevAutoCalcRef = useRef(!!workItem?.CalculateAmountsAutomatically);

    // Snapshot / restore the manual value on toggle transitions.
    useEffect(() => {
        const wasOn = prevAutoCalcRef.current;
        const isOn = !!workItem?.CalculateAmountsAutomatically;
        prevAutoCalcRef.current = isOn;
        if (!wasOn && isOn) {
            setManualToBillBackup(workItem?.ToBillAmount);
        } else if (wasOn && !isOn) {
            if (manualToBillBackup !== undefined) {
                updateInvoice(
                    manualToBillBackup,
                    'json', 'ListItem', workItem?._view_id, 'ToBillAmount',
                );
                setManualToBillBackup(undefined);
            }
            setAutoCalcInfo({ rate_source: null, reason: null });
        }
    }, [workItem?.CalculateAmountsAutomatically]);

    // Debounced auto-calc fetch. Fires whenever the toggle is on and any
    // input that feeds the calc changes (matter, cost type, quantity).
    useEffect(() => {
        if (!isAderant) return undefined;
        if (!workItem?.CalculateAmountsAutomatically) return undefined;
        const url = buildRateLookupUrl({
            matter_id: workItem?.MatterId,
            cost_type: workItem?.CostType,
            quantity: workItem?.WorkQuantity,
        });
        if (!url) {
            // Missing matter or cost type — keep the field editable, no
            // request to fire. The hint is the absence of a source.
            setAutoCalcInfo({ rate_source: null, reason: null });
            return undefined;
        }
        const timer = setTimeout(() => {
            cachedFetch(url, 'GET', {}, (response) => {
                const r = response?.data ?? {};
                setAutoCalcInfo({
                    rate_source: r.rate_source ?? null,
                    reason: r.reason ?? null,
                });
                if (r.to_bill_amount != null) {
                    updateInvoice(
                        r.to_bill_amount,
                        'json', 'ListItem', workItem?._view_id, 'ToBillAmount',
                    );
                }
            });
        }, AUTO_CALC_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [
        isAderant,
        workItem?.CalculateAmountsAutomatically,
        workItem?.MatterId,
        workItem?.CostType,
        workItem?.WorkQuantity,
    ]);

    useEffect(() => {
        if (!isAderant) return;
        const probed = PROBED_DROPDOWN_FIELDS;
        Object.entries(probed).forEach(([field, loader]) => {
            // Skip the probe if neither this field nor any of its aliases is
            // in the requested set — avoids spurious /client/options/ calls
            // when the caller filters down to e.g. just `Comments`.
            const aliases = field === 'Employee' ? EMPLOYEE_FLAVORS : [field];
            if (fields.length > 0 && !aliases.some((f) => fields.includes(f))) {
                return;
            }
            loader('', (rows) => {
                const count = rows?.length ?? 0;
                setOptionCounts((prev) => ({ ...prev, [field]: count }));
                // XPEN-3025: remember the sole row so it can auto-fill + lock.
                if (AUTO_LOCK_WHEN_SINGLE_OPTION.has(field) && count === 1) {
                    setSingleOption((prev) => ({ ...prev, [field]: rows[0] }));
                }
            });
        });
        // Probe is one-shot per tenant + visible-fields combo. `cachedFetch`
        // dedupes the underlying HTTP call across re-renders / siblings.
    }, [isAderant, fields]);

    const hasOptions = (field) => {
        const probeKey = EMPLOYEE_FLAVORS.includes(field) ? 'Employee' : field;
        return !!optionCounts[probeKey];
    };

    // XPEN-3025: when the probe captured a single valid option for a
    // sole-option field, apply it to the current line if the line has not
    // been set yet. Runs whenever the sole option or the active line
    // changes; guarded by _view_id so switching lines re-applies.
    useEffect(() => {
        if (!workItem) return;
        AUTO_LOCK_WHEN_SINGLE_OPTION.forEach((field) => {
            const sole = singleOption[field];
            const jsonKey = AUTO_LOCK_JSON_KEY_BY_FIELD[field];
            if (!sole || !jsonKey) return;
            if (workItem[jsonKey]) return;
            updateInvoice(
                toPersistableOption({ value: sole.value, label: sole.label }),
                'json', 'ListItem', workItem._view_id, jsonKey,
            );
        });
    }, [singleOption, workItem?._view_id]);

    const lockForFocus = (fieldName) => {
        return saving || locked || (focusMode && fieldName !== "Comments");
    };

    // XPEN-3568: Single / Multiple on the GL Timekeeper (Elite) and Employee (Aderant)
    // fields. `renderPicker` builds the field's picker; row pickers (called with a third
    // `options` argument) get a row name distinct from the Single picker's, and never
    // save the line themselves.
    const splitField = getLineSplitField(data, workItem);
    const lineViewId = workItem?._view_id;
    const splitOffered = Boolean(onApplyLineSplit && splitField && workItem);
    // Shown only when both the notice and the line carry a real, matching id.
    const noticeForLine = lineSplitNotice && lineSplitNotice.viewId != null && lineViewId != null
        && lineSplitNotice.viewId === lineViewId ? lineSplitNotice.text : null;
    const renderLineSplit = (fieldName, value, onSingleChange, renderPicker) => (
        <LineSplitInput
            // Rows are seeded once, so the control restarts per line — only when it can split;
            // otherwise the picker keeps today's identity across a line switch.
            key={splitOffered ? lineViewId : undefined}
            data={data}
            splitField={splitField}
            renderPicker={renderPicker}
            value={value}
            onSingleChange={onSingleChange}
            onApplySplit={splitOffered ? (rows) => onApplyLineSplit(workItem, rows) : undefined}
            isDisabled={lockForFocus(fieldName) || !workItem}
            splitDisabledReason={lineSplitDisabledReason}
            splitNotice={noticeForLine}
        />
    );

    const updateWorkItemWithTotal = (nextPartial) => {
        if (!workItem) return;
        const nextItem = { ...workItem, ...nextPartial };
        const qty = toNumber(nextItem.WorkQuantity);
        const rate = toNumber(nextItem.WorkRate);
        const computed_work_total = qty * rate;
        nextItem.WorkTotal = computed_work_total;
        // Keep the in-modal item snapshot aligned so consecutive edits use the latest values.
        Object.assign(workItem, nextPartial, { WorkTotal: computed_work_total });
        updateInvoice(
            { ...nextItem},
            "json",
            "ListItem",
            workItem._view_id
        );
    };

    const renderDropdownField = (field, jsonKey, labelKey, fallbackLabel, loader) => {
        // The per-format answer lives in one place (Aderant dropdowns).
        if (!isEditorAdditionalFieldAllowed(data, field, { tableKind: 'billable' }) || !shouldShowField(field)) return null;
        // Hide while probing (undefined) and if upstream returned zero rows
        // for this tenant. Avoids dead dropdowns when a master like
        // /Locations is empty for the firm.
        if (!hasOptions(field)) return null;
        // XPEN-3025: sole valid option → prefilled + locked (auto-fill runs
        // in the effect above). isClearable stays off in the locked case so
        // the user cannot wipe the auto-filled value.
        const singleLock =
            AUTO_LOCK_WHEN_SINGLE_OPTION.has(field) && optionCounts[field] === 1;
        const labelText = data.t.read(labelKey) || fallbackLabel;
        return (
            <div className={"d-flex align-content-center mb-3"} key={field}>
                <label className={`col-3 my-auto ${boldLabelClass(field)}`.trim()}>{labelText}</label>
                <div className={`col-8 border p-0 rounded-lg ${boldValueClass(field)}`.trim()}>
                    <AsyncSelect
                        styles={Utils.customStyleSelect}
                        menuPortalTarget={document.body}
                        value={toSelectOption(workItem?.[jsonKey])}
                        onChange={(choice) => updateInvoice(choice ? toPersistableOption(choice) : undefined, "json", "ListItem", workItem._view_id, jsonKey)}
                        classNamePrefix="select"
                        isDisabled={lockForFocus(field) || !workItem || singleLock}
                        name={`${field}-select`}
                        placeholder={labelText}
                        isClearable={!singleLock}
                        defaultOptions
                        cacheOptions
                        loadOptions={loader}
                    />
                </div>
            </div>
        );
    };

    // Same shape as renderDropdownField, but selecting an employee also
    // refreshes Office / GLOffice / DepartmentId on the line — only when
    // `sourcesLineOrg` (Employee, IncurringEmployee); the approver flavor
    // (AuthorizingEmployee) never touches line GL coding.
    const renderEmployeeDropdown = (field, jsonKey, labelKey, fallbackLabel, sourcesLineOrg) => {
        if (!isAderant || !shouldShowField(field)) return null;
        if (!hasOptions(field)) return null;
        const labelText = data.t.read(labelKey) || fallbackLabel;
        const renderEmployeePicker = (value, onPick, options) => (
            <AsyncSelect
                styles={Utils.customStyleSelect}
                menuPortalTarget={document.body}
                value={toSelectOption(value)}
                onChange={onPick}
                classNamePrefix="select"
                isDisabled={options?.isDisabled || lockForFocus(field) || !workItem}
                name={options ? `${field}-select-row` : `${field}-select`}
                placeholder={labelText}
                isClearable
                defaultOptions
                cacheOptions
                loadOptions={loadEmployees}
            />
        );
        const onChange = (choice) => {
            const persisted = choice ? toPersistableOption({ value: choice.value, label: choice.label }) : undefined;
            updateInvoice(persisted, "json", "ListItem", workItem._view_id, jsonKey);
            if (!sourcesLineOrg || !choice) return;
            const office = (choice.office || '').trim();
            updateInvoice(office || null, "json", "ListItem", workItem._view_id, "Office");
            const department = (choice.department || '').trim();
            updateInvoice(
                department ? { value: department, label: department } : null,
                "json", "ListItem", workItem._view_id, "DepartmentId",
            );
        };
        return (
            <div className={"d-flex align-content-center mb-3"} key={field}>
                <label className={`col-3 my-auto ${boldLabelClass(field)}`.trim()}>{labelText}</label>
                <div className={`col-8 border p-0 rounded-lg ${boldValueClass(field)}`.trim()}>
                    {field === 'Employee'
                        ? renderLineSplit(field, workItem?.[jsonKey], onChange, renderEmployeePicker)
                        : renderEmployeePicker(workItem?.[jsonKey], onChange)}
                </div>
            </div>
        );
    };

    const renderTextField = (field, jsonKey, labelKey, fallbackLabel, format = "text", maxLength) => {
        if (!isAderant || !shouldShowField(field)) return null;
        return (
            <div className={"d-flex align-content-center mb-3"} key={field}>
                <label className={`col-3 my-auto ${boldLabelClass(field)}`.trim()}>{data.t.read(labelKey) || fallbackLabel}</label>
                <div className={`col-8 border p-0 rounded-lg ${boldValueClass(field)}`.trim()}>
                    <FormTextField
                        format={format}
                        maxLength={maxLength}
                        locked={lockForFocus(field)}
                        value={workItem?.[jsonKey]}
                        validateChange={(val) => updateInvoice(val === '' ? undefined : val, "json", "ListItem", workItem._view_id, jsonKey)}
                    />
                </div>
            </div>
        );
    };

    const renderToBillAmountField = () => {
        const field = 'ToBillAmount';
        if (!isAderant || !shouldShowField(field)) return null;
        const autoCalcOn = !!workItem?.CalculateAmountsAutomatically;
        const lockedForAutoCalc = shouldLockToBillForAutoCalc(autoCalcOn, autoCalcInfo);
        const labelText = data.t.read('invoice.to_bill_amount') || 'To-Bill Amount';
        const hint = autoCalcOn
            ? formatRateSourceHint(autoCalcInfo, (k, fb) => data.t.read(k) || fb)
            : null;
        const onChange = (val) => {
            // Typing while the toggle is on (only possible in the no-rate
            // path; lock blocks edits otherwise) means the user has chosen
            // a manual value over whatever auto-calc would've produced.
            // Drop the pre-toggle backup so toggle-off doesn't stomp the
            // value they just typed.
            if (autoCalcOn) {
                setManualToBillBackup(undefined);
            }
            updateInvoice(val === '' ? undefined : val, "json", "ListItem", workItem._view_id, "ToBillAmount");
        };
        return (
            <div className={"mb-3"} key={field}>
                <div className={"d-flex align-content-center"}>
                    <label className={`col-3 my-auto ${boldLabelClass(field)}`.trim()}>{labelText}</label>
                    <div className={`col-8 border p-0 rounded-lg ${boldValueClass(field)}`.trim()}>
                        <FormTextField
                            format="money"
                            locked={lockForFocus(field) || lockedForAutoCalc}
                            value={workItem?.ToBillAmount}
                            validateChange={onChange}
                        />
                    </div>
                </div>
                {hint != null && (
                    <div
                        className={"small text-muted offset-3 col-8 pl-2 pt-1"}
                        data-testid="to-bill-rate-source-hint"
                    >
                        {hint}
                    </div>
                )}
            </div>
        );
    };

    const renderAutoCalculationField = () => {
        const field = 'CalculateAmountsAutomatically';
        if (!isAderant || !shouldShowField(field)) return null;
        const inputId = `${field}-${workItem?._view_id ?? 'default'}`;
        const labelText = data.t.read('invoice.auto_calculation') || 'Auto Calculation';
        return (
            <div className={"d-flex align-items-center mb-3"} key={field}>
                <label className={`col-3 my-auto ${boldLabelClass(field)}`.trim()} htmlFor={inputId}>{labelText}</label>
                <div className={`custom-control custom-switch switch-brand ${boldValueClass(field)}`.trim()}>
                    <input
                        id={inputId}
                        type="checkbox"
                        role="switch"
                        className="custom-control-input"
                        disabled={lockForFocus(field)}
                        checked={!!workItem?.CalculateAmountsAutomatically}
                        onChange={(e) => updateInvoice(e.target.checked || undefined, "json", "ListItem", workItem._view_id, field)}
                    />
                    <label className="custom-control-label" htmlFor={inputId}></label>
                </div>
            </div>
        );
    };

    return (
            <div>
                {shouldShowField('WorkQuantity') && (
                    <div className={"d-flex align-content-center mb-3"}>
                        <label className={`col-3 my-auto ${boldLabelClass('WorkQuantity')}`.trim()}>
                            {data.t.read("invoice.work_quantity")}
                        </label>
                        <div className={`col-8 border p-0 rounded-lg ${boldValueClass('WorkQuantity')}`.trim()}>
                            <FormTextField
                                format="number"
                                mathOperations={true}
                                locked={lockForFocus("WorkQuantity")}
                                value={workItem.WorkQuantity}
                                validateChange={val => fields.includes('WorkTotal') ? updateWorkItemWithTotal({ WorkQuantity: val }) : updateInvoice(val, "json", "ListItem", workItem._view_id, "WorkQuantity")}
                            />
                        </div>
                    </div>
                )}
                {!isAderant && shouldShowField('WorkRate') && (
                    <div className={"d-flex align-content-center mb-3"}>
                        <label className={`col-3 my-auto ${boldLabelClass('WorkRate')}`.trim()}>
                            {data.t.read("invoice.work_rate")}
                        </label>
                        <div className={`col-8 border p-0 rounded-lg ${boldValueClass('WorkRate')}`.trim()}>
                            <FormTextField
                                format="money"
                                locked={lockForFocus("WorkRate")}
                                value={workItem.WorkRate}
                                validateChange={val => fields.includes('WorkTotal') ? updateWorkItemWithTotal({ WorkRate: val }) : updateInvoice(val, "json", "ListItem", workItem._view_id, "WorkRate")}
                            />
                        </div>
                    </div>
                )}
                {shouldShowField('WorkTotal') && (
                    <div className={"d-flex align-content-center mb-3"}>
                        <label className={`col-3 my-auto ${boldLabelClass('WorkTotal')}`.trim()}>
                            {data.t.read("invoice.work_total") || "Work Total"}
                        </label>
                        <div className={`col-8 border p-0 rounded-lg ${boldValueClass('WorkTotal')}`.trim()}>
                            <FormTextField
                                format="money"
                                locked={true}
                                value={workItem.WorkTotal}
                                validateChange={val => updateInvoice(val, "json", "ListItem", workItem._view_id, "WorkTotal")}
                            />
                        </div>
                    </div>
                )}
                {renderEmployeeDropdown('Employee', 'EmployeeId', 'invoice.employee', 'Employee', true)}
                {renderEmployeeDropdown('IncurringEmployee', 'IncurringEmployeeId', 'invoice.incurring_employee', 'Incurring Employee', true)}
                {renderEmployeeDropdown('AuthorizingEmployee', 'AuthorizingEmployeeId', 'invoice.authorizing_employee', 'Authorizing Employee', false)}
                {shouldShowField('Office') && Utils.isEngineType(data, 'legal') && (
                    <div className={"d-flex align-content-center mb-3"}>
                    <label className={`col-3 my-auto ${boldLabelClass('Office')}`.trim()}>
                        {data.t.read("office")}
                    </label>
                    <div className={`col-8 border p-0 rounded-lg ${boldValueClass('Office')}`.trim()}>
                        <AsyncSelect
                                key={`office-select-${workItem?._view_id || 'default'}-${workItem?.Office || ''}`}
                                styles={Utils.customStyleSelect}
                                menuPortalTarget={document.body}
                                value={workItem?.Office ? { label: workItem.Office, value: workItem.Office } : ''}
                                onChange={(choice) => {
                                    updateInvoice(choice ? choice.value : null, "json", "ListItem", workItem._view_id, "Office");
                                }}
                                classNamePrefix="select"
                                isDisabled={lockForFocus("Office") || !workItem}
                                name="office-select"
                                placeholder={data.t.read("office")}
                                isClearable
                                defaultOptions
                                cacheOptions
                                loadOptions={loadOffices}
                            />

                    </div>
                </div>
                )}
                {renderDropdownField('Department', 'DepartmentId', 'invoice.department', 'Department', loadDepartments)}
                {!isAderant && data?.config?.gl_account_segment_configuration?.GLDepartement?.active === true && shouldShowField('GLDepartement') && (
                    <div className={"d-flex align-content-center mb-3"}>
                        <label className={`col-3 my-auto ${boldLabelClass('GLDepartement')}`.trim()}>
                            {data.t.read("invoice.gl_departement")}
                        </label>
                        <div className={`col-8 border p-0 rounded-lg ${boldValueClass('GLDepartement')}`.trim()}>
                        <FormTextField format="text" locked={lockForFocus("GLDepartement")} value={workItem.GLDepartement} validateChange={val => updateInvoice(val, "json", "ListItem", workItem._view_id, "GLDepartement")} />
                    </div>
                </div>
                )}
                {!isAderant && !isBillableLine && data?.config?.gl_account_segment_configuration?.GLSection?.active === true && shouldShowField('GLSection') && (
                    <div className={"d-flex align-content-center mb-3"}>
                        <label className={`col-3 my-auto ${boldLabelClass('GLSection')}`.trim()}>
                            {data.t.read("invoice.gl_section")}
                        </label>
                        <div className={`col-8 border p-0 rounded-lg ${boldValueClass('GLSection')}`.trim()}>
                            <GlSegmentSelect
                                segment="GLSection"
                                value={workItem?.GLSection}
                                onChange={(next) => updateInvoice(next, "json", "ListItem", workItem._view_id, "GLSection")}
                                isDisabled={lockForFocus("GLSection") || !workItem}
                                name="gl-section-select"
                                placeholder={data.t.read("invoice.gl_section")}
                            />
                        </div>
                    </div>
                )}
                {isElite && !isBillableLine && data?.config?.gl_account_segment_configuration?.GLPracticeGroup?.active === true && shouldShowField('GLPracticeGroup') && (
                    <div className={"d-flex align-content-center mb-3"}>
                        <label className={`col-3 my-auto ${boldLabelClass('GLPracticeGroup')}`.trim()}>
                            {data.t.read("invoice.gl_practice_group")}
                        </label>
                        <div className={`col-8 border p-0 rounded-lg ${boldValueClass('GLPracticeGroup')}`.trim()}>
                            <GlSegmentSelect
                                segment="GLPracticeGroup"
                                value={workItem?.GLPracticeGroup}
                                onChange={(next) => updateInvoice(next, "json", "ListItem", workItem._view_id, "GLPracticeGroup")}
                                isDisabled={lockForFocus("GLPracticeGroup") || !workItem}
                                name="gl-practice-group-select"
                                placeholder={data.t.read("invoice.gl_practice_group")}
                            />
                        </div>
                    </div>
                )}
                {isElite && !isBillableLine && data?.config?.gl_account_segment_configuration?.GLTimekeeper?.active === true && shouldShowField('GLTimekeeper') && (
                    <div className={"d-flex align-content-center mb-3"}>
                        <label className={`col-3 my-auto ${boldLabelClass('GLTimekeeper')}`.trim()}>
                            {data.t.read("invoice.gl_timekeeper")}
                        </label>
                        <div className={`col-8 border p-0 rounded-lg ${boldValueClass('GLTimekeeper')}`.trim()}>
                            {renderLineSplit(
                                "GLTimekeeper",
                                workItem?.GLTimekeeper,
                                (next) => updateInvoice(next, "json", "ListItem", workItem._view_id, "GLTimekeeper"),
                                (value, onPick, options) => (
                                    <GlSegmentSelect
                                        segment="GLTimekeeper"
                                        value={value}
                                        onChange={onPick}
                                        isDisabled={options?.isDisabled || lockForFocus("GLTimekeeper") || !workItem}
                                        name={options ? "gl-timekeeper-select-row" : "gl-timekeeper-select"}
                                        placeholder={data.t.read("invoice.gl_timekeeper")}
                                    />
                                ),
                            )}
                        </div>
                    </div>
                )}
                {!isAderant && shouldShowField('Activity') && (
                    <div className={"d-flex align-content-center mb-3"}>
                        <label className={`col-3 my-auto ${boldLabelClass('Activity')}`.trim()}>
                            {data.t.read("invoice.activity")}
                        </label>
                        <div className={`col-8 border p-0 rounded-lg ${boldValueClass('Activity')}`.trim()}>
                        <AsyncSelect
                            key={`activity-select-${workItem?._view_id || 'default'}-${workItem?.MatterId || ''}`}
                            styles={Utils.customStyleSelect}
                            menuPortalTarget={document.body}
                            value={activityMap(workItem?.Activity)}
                            onChange={(choice) => {
                                updateInvoice(choice ? choice.value : null, "json", "ListItem", workItem._view_id, "Activity");
                            }}
                            classNamePrefix="select"
                            isDisabled={lockForFocus("Activity") || !workItem}
                            name="activity-select"
                            placeholder={data.t.read("invoice.activity")}
                            isClearable
                            defaultOptions
                            cacheOptions
                            loadOptions={loadActivities}
                        />

                        </div>
                    </div>
                )}

                {renderDropdownField('ProfitCenter', 'ProfitCenterId', 'invoice.profit_center', 'Profit Center', loadProfitCenters)}
                {renderDropdownField('Jurisdiction', 'JurisdictionId', 'invoice.jurisdiction', 'Jurisdiction', loadJurisdictions)}
                {renderTextField('HoldDate', 'HoldDate', 'invoice.hold_date', 'Hold Date', 'date')}
                {renderDropdownField('Location', 'LocationId', 'invoice.location', 'Location', loadLocations)}
                {renderDropdownField('Project', 'ProjectId', 'invoice.project', 'Project', loadProjects)}
                {renderTextField('Reference', 'Reference', 'invoice.reference', 'Reference', 'text', ADERANT_LINE_REFERENCE_MAXLENGTH)}
                {renderAutoCalculationField()}
                {renderToBillAmountField()}
                {renderTextField('PreBillDisbursementNarrative', 'PreBillDisbursementNarrative', 'invoice.pre_bill_narrative', 'Pre-Bill Narrative')}

                {!isAderant && shouldShowField('Comments') && (
                    <div className={"d-flex align-content-center mb-3"}>
                    <label className={`col-3 my-auto ${boldLabelClass('Comments')}`.trim()}>
                        {data.t.read("invoice.comments")}{isCommentsRequired ? <span> *</span> : null}
                    </label>
                    <div className={`col-8 border p-0 rounded-lg ${boldValueClass('Comments')}`.trim()}>
                        <FormTextField
                            format="text"
                            locked={lockForFocus("Comments")}
                            classes={focusMode ? "border-highlight-secondary" : ""}
                            value={workItem.Comments}
                            validateChange={val => updateInvoice(val, "json", "ListItem", workItem._view_id, "Comments")}
                        />
                    </div>
                </div>
                )}

            </div>
    );
};

export default inject('data')(AdditionalFieldsModal);
